'use client';

import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Button from './Button';

export type SelectOption = {
  value: string;
  label: string;
};

export type SelectProps = {
  name?: string;
  value?: string;
  defaultValue?: string;
  options: SelectOption[];
  placeholder?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  className?: string;
  id?: string;
  disableOptionTextScroll?: boolean;
};

export default function Select({
  name,
  value: controlledValue,
  defaultValue,
  options,
  placeholder = '',
  onChange,
  disabled = false,
  className = '',
  id,
  disableOptionTextScroll = false,
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [internalValue, setInternalValue] = useState<string>(defaultValue || controlledValue || '');
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isControlled = controlledValue !== undefined;
  const currentValue = isControlled ? controlledValue : internalValue;

  const updateDropdownPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + 8,
      left: rect.left,
      width: Math.max(rect.width, 200),
    });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      setDropdownPosition(null);
      return;
    }
    updateDropdownPosition();
    const handleScrollOrResize = () => updateDropdownPosition();
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    let rafId = 0;
    const tick = () => {
      updateDropdownPosition();
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
      cancelAnimationFrame(rafId);
    };
  }, [isOpen, updateDropdownPosition]);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    const target = e.target as Node;
    if (triggerRef.current?.contains(target) || dropdownRef.current?.contains(target)) return;
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, handleClickOutside]);

  // Update internal value when controlled value changes
  useEffect(() => {
    if (isControlled && controlledValue !== currentValue) {
      setInternalValue(controlledValue);
    }
  }, [controlledValue, isControlled]);

  const handleSelect = (optionValue: string) => {
    if (!isControlled) {
      setInternalValue(optionValue);
    }
    if (onChange) {
      onChange(optionValue);
    }
    setIsOpen(false);
  };

  const selectedOption = options.find(opt => opt.value === currentValue);
  const displayText = selectedOption ? selectedOption.label : (placeholder || 'Select...');

  const isFullWidth = className?.includes('w-full');
  const triggerClasses = [
    'group flex min-h-[40px] w-full items-center justify-between rounded-lg border border-neutral-10 bg-constant-white px-3 py-2 text-left text-[14px] text-text-primary transition-[border-color,box-shadow,background-color] hover:border-blue-15 hover:bg-bg-0',
    'focus:outline-none',
    'disabled:bg-bg-1 disabled:text-text-tertiary disabled:cursor-not-allowed',
    isFullWidth ? 'w-full' : 'min-w-max',
    className,
  ].filter(Boolean).join(' ');

  const dropdownContent =
    isOpen &&
    dropdownPosition &&
    typeof document !== 'undefined' &&
    ((
      <div
        ref={dropdownRef}
        role="listbox"
        aria-label="Options"
        className="fixed z-[9999] max-h-60 overflow-y-auto overflow-x-hidden rounded-lg border border-neutral-10 bg-constant-white shadow-md py-0.5 min-w-[160px]"
        style={{
          position: 'fixed',
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          width: dropdownPosition.width,
        }}
      >
        {placeholder && (
          <Button
            raw
            type="button"
            onClick={() => handleSelect('')}
            className={`w-full px-3 py-2 text-left text-[14px] whitespace-nowrap transition-colors focus:outline-none ${
              currentValue === '' ? 'bg-blue-5 font-medium text-blue-100 hover:bg-blue-10' : 'text-text-primary hover:bg-blue-5'
            }`}
          >
            {placeholder}
          </Button>
        )}
        {options.map((option) => {
          const isLongText = !disableOptionTextScroll && option.label.length > 20;
          return (
            <Button
              raw
              key={option.value}
              type="button"
              onClick={() => handleSelect(option.value)}
              role="option"
              aria-selected={currentValue === option.value}
              className={`group flex w-full items-center justify-between border-b border-neutral-10 px-3 py-2 text-left text-[14px] transition-colors last:border-b-0 focus:outline-none ${
                currentValue === option.value
                  ? 'bg-blue-5 text-blue-100 font-medium hover:bg-blue-10'
                  : 'text-text-primary hover:bg-blue-5'
              }`}
            >
              <div className="scroll-text-container min-w-0 flex-1">
                {isLongText ? (
                  <>
                    <span className="truncate block group-hover:hidden" title={option.label}>
                      {option.label}
                    </span>
                    <span className="hidden group-hover:inline-block scroll-text-animated" title={option.label}>
                      {option.label} • {option.label}
                    </span>
                  </>
                ) : (
                  <span className="truncate block" title={option.label}>
                    {option.label}
                  </span>
                )}
              </div>
              {currentValue === option.value && (
                <svg className="w-4 h-4 text-blue-100 flex-shrink-0 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </Button>
          );
        })}
      </div>
    ));

  return (
    <div className={`relative ${isFullWidth ? 'block w-full' : 'inline-block'}`} id={id}>
      {/* Hidden native select for form submission */}
      {name && (
        <select
          name={name}
          value={currentValue}
          onChange={() => {}}
          className="sr-only bg-constant-white text-text-primary"
          disabled={disabled}
          tabIndex={-1}
          aria-hidden="true"
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}

      {/* Single bordered trigger: text + icon (no extra wrapper) */}
      <Button
        raw
        ref={triggerRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={triggerClasses}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className={`block min-w-0 flex-1 truncate text-left text-sm font-medium ${selectedOption ? 'text-text-primary' : 'text-text-tertiary group-hover:text-text-primary'}`}>
          {displayText}
        </span>
        <span className="pointer-events-none flex-shrink-0 ml-2 flex items-center">
          <svg
            className={`h-4 w-4 text-text-tertiary transition-[transform,color] duration-200 group-hover:text-text-primary ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </Button>

      {typeof document !== 'undefined' && createPortal(dropdownContent ?? null, document.body)}
    </div>
  );
}

